import { ReactNode, useEffect } from 'react'

import { useBoolean, useDataMutation, useNotification, useQuery, useRoles } from 'hooks'
import { logToAnalytics } from 'modules'
import { DialogBody } from 'UI/Components'

import { Box, makeStyles, Typography } from '@material-ui/core'

import LimitationImage from 'assets/images/locked-page.png'

const useStyles = makeStyles(theme => ({
    redText: {
        color: theme.palette.primary.main,
        marginTop: theme.spacing(2)
    },
    textWrapper: {
        maxHeight: '130px',
        overflow: 'auto'
    }
}))

const yellow = 'rgba(201, 222, 1, 0.9)'
const blue = 'rgba(0, 188, 212, 0.9)'

type Props = {
    children: ReactNode
    setLoading?: (loading: boolean) => void
}
export const ProtectedViewerAction = ({ children, setLoading = () => {} }: Props) => {
    const classes = useStyles()

    const { showErrorNotification } = useNotification()

    const dialogShown = useBoolean()

    const { isViewer } = useRoles()

    const { data, isValidating, mutate } = useQuery<{ allowed: boolean; admins?: string[] }>(
        '/c/v1/viewer-upgrade/get-status',
        {
            method: 'GET'
        },
        {
            revalidateOnMount: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateIfStale: false,
            onSuccess: data => {
                dialogShown.setTrue()
                logToAnalytics('videoportal_capture_click_successful', data)
            },
            onError: e => {
                showErrorNotification('Something went wrong. Please try again later')
                logToAnalytics('videoportal_capture_click_failed', e)
            }
        }
    )

    useEffect(() => {
        setLoading(isValidating)
    }, [isValidating, setLoading])

    const { mutate: upgradeToAdmin } = useDataMutation('/c/v1/viewer-upgrade', 'POST')

    const dialogProps = data?.allowed
        ? {
              title: 'Lace up and get in the game',
              text: `Create your own video spaces and share it with your team members.`,
              primaryText: 'Create your own account',
              onPrimaryClick: () => {
                  logToAnalytics('videoportal_create_account_click')
                  upgradeToAdmin({})
                      .then(dialogShown.setFalse)
                      .catch(e => {
                          showErrorNotification('Something went wrong. Please try again later')
                          logToAnalytics('videoportal_upgrade_failed', e)
                      })
              }
          }
        : {
              title: 'Lace up and start recording',
              text: `You can view everything, but to start capturing, contact ${
                  data?.admins?.length === 1
                      ? data.admins[0]
                      : data?.admins?.sort().slice(0, 2).join(' or ') || 'admins'
              }.`,
              primaryText: 'Contact your admin',
              onPrimaryClick: dialogShown.setFalse
          }

    return (
        <>
            <Box
                onClick={() => {
                    if (isViewer) {
                        mutate()
                    }
                }}
                style={{ cursor: 'pointer' }}
            >
                <Box style={{ pointerEvents: isViewer ? 'none' : 'auto' }}>{children}</Box>
            </Box>

            {dialogShown.isTrue && (
                <DialogBody
                    isOpen
                    {...dialogProps}
                    bubblesBackground={[yellow, blue]}
                    image={LimitationImage}
                    skipAdmin={true}
                    maxTextWidth="400px"
                    text={
                        <Typography className={classes.textWrapper}>{dialogProps.text}</Typography>
                    }
                    secondaryButton={
                        <Typography className={classes.redText}>No credit card required</Typography>
                    }
                    onClose={dialogShown.setFalse}
                />
            )}
        </>
    )
}
